import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Pagination,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

import CustomizedTables, {
  StyledTableCell,
  StyledTableRow,
} from "../../Component/UI/Table/Table";
import Modal from "../../Component/Modal/Modal";
import { CategoryAnalysisContext } from "./category-analysis-context";
import { AuthContext } from "../../context/auth-context";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";
import useCAHistory from "./useCAHistory";
import api from "../../BrandOnBoarding/api";

import classes from "../BrandAudit/History/HistoryV2.module.css";

const PAGE_LIMIT = 5;

export default function CAHistory({
  setCancelTransaction = () => {},
  startPolling = () => {},
  stopPolling = () => {},
  setActiveTab = () => {},
  setIsAuditRunning = () => {},
  loadCategoryAnalysis,
  open,
  onClose,
  onCAModeChange = () => {},
  isBaselineCategoryAnalysisEnabled = false,
  onBaselineCAHtmlReportChange = () => {},
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const caCtx = useContext(CategoryAnalysisContext);
  const { user, selectedCustomerId } = useContext(AuthContext);
  const debounceTimerRef = useRef(null);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;

  const {
    history,
    setHistory,
    loading,
    getHistory,
    deleteHistory,
    error,
    totalCount,
    totalPages,
  } = useCAHistory();

  const { page, setPage } = caCtx;

  // Debounce search
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery]);

  // Search
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery.trim()) {
        setIsSearchMode(false);
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchTotalPages(0);
        return;
      }
      setIsSearchMode(true);
      setSearchLoading(true);
      try {
        const response = await api.get(
          `${webSocketBaseURL}/search-history/categoryAnalysis/brand/${selectedCustomerId}/${user?.user_role?.user_id}`,
          { params: { search: debouncedSearchQuery, page, limit: PAGE_LIMIT } }
        );
        const { data = [], totalCount = 0, totalPages = 0 } = response.data;
        caCtx.updateTransactionHistory(data);
        setSearchResults(data);
        setSearchTotalCount(totalCount);
        setSearchTotalPages(totalPages);
      } catch {
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchTotalPages(0);
      } finally {
        setSearchLoading(false);
      }
    };
    performSearch();
  }, [debouncedSearchQuery, page, selectedCustomerId, user?.user_role?.user_id, webSocketBaseURL]);

  // Load history on page change
  useEffect(() => {
    if (isSearchMode) return;
    if (!searchQuery.trim()) {
      getHistory(page, PAGE_LIMIT);
    }
  }, [page, isSearchMode]);

  // Sync history from context
  useEffect(() => {
    const contextHistory = caCtx?.state?.transactionHistory;
    if (Array.isArray(contextHistory)) {
      setHistory(contextHistory);
    }
  }, [caCtx.state?.transactionHistory]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setIsSearchMode(false);
    setSearchResults([]);
    setPage(1);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
    const tableWrapper = document.querySelector(`.${classes.tableWrapper}`);
    if (tableWrapper) tableWrapper.scrollTop = 0;
  };

  const deleteHistoryItemHandler = () => {
    if (!itemToDelete) return;

    const deletedId = String(itemToDelete.transactionId ?? "");
    const activeIds = [caCtx.state?.id, caCtx.state?.transactionId]
      .filter(Boolean)
      .map(String);
    const isDeletingActiveRecord = activeIds.includes(deletedId);

    if (isDeletingActiveRecord) {
      setCancelTransaction(true);
      stopPolling();
      caCtx.resetState();
    }
    deleteHistory(itemToDelete.transactionId);
    setShowDeleteDialog(false);
    setItemToDelete(null);
    setTimeout(() => {
      if (isSearchMode) {
        setDebouncedSearchQuery("");
        setTimeout(() => setDebouncedSearchQuery(searchQuery), 100);
      } else {
        getHistory(page, PAGE_LIMIT);
      }
    }, 500);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  const getTransactionData = async (transactionRecord) => {
    console.log("transactionRecord",transactionRecord)
    onClose();
    stopPolling();
    caCtx.resetState();
    caCtx.updateLoading(true);

    const completedTests = transactionRecord.testIds.filter(
      (t) => t.status !== "INACTIVE"
    );
    const transactionId =
      completedTests[completedTests.length - 1]?.transactionId;
    setActiveTab(completedTests.length - 1);
    setIsAuditRunning(false);

    transactionRecord?.testIds
      .map((item, originalIndex) => ({ ...item, originalIndex }))
      .filter((item) => item.status !== "INACTIVE")
      .forEach((item) => {
        if (transactionRecord?.transactionId === item.transactionId) {
          caCtx.updateLaunchRecord({
            name: "Final Analysis",
            transactionId: item.transactionId,
          });
        } else {
          caCtx.updateLaunchRecord({
            name: `Test Analysis ${item.originalIndex + 1}`,
            transactionId: item.transactionId,
            totalTestAudit: transactionRecord?.testIds?.length,
          });
        }
      });

    const entityLabel = transactionRecord?.category?.[0] || "";
    const resolvedMarket =
      transactionRecord?.market?.length > 0
        ? transactionRecord.market
        : ["General Market"];
    const resolvedPersona =
      Array.isArray(transactionRecord?.persona) && transactionRecord.persona.length > 0
        ? transactionRecord.persona
        : [{ title: "General Persona", description: "General Persona" }];

    // Set dimensions first so REFRESH_STATE initializes brand_scores / core_attributes /
    // category_dimensions with the correct llm × category × market × persona keys.
    onCAModeChange(transactionRecord?.caMode || "full");
    onBaselineCAHtmlReportChange(transactionRecord?.is_baseline_html_reporting_enabled || false);
    caCtx.updateCategory(transactionRecord?.category);
    caCtx.updateLLM(transactionRecord?.llm);
    caCtx.updateMarket(resolvedMarket);
    caCtx.updatePersona(resolvedPersona);
    caCtx.refreshState();

    // Set the rest of the context state
    caCtx.updateTransactionId(transactionId);
    caCtx.updateFinalTransactionId(transactionRecord.transactionId);
    caCtx.cancelSelection();
    caCtx.updateEntity("");
    caCtx.updatePrimaryLLM(transactionRecord?.primaryLLM);
    caCtx.updatedCompetitors({ ...(transactionRecord.competitor || {}) });
    caCtx.updateSelectedLLM(transactionRecord?.primaryLLM);
    caCtx.updateSourceValidationStatus(transactionRecord?.sourceValidationStatus);
    caCtx.updateSelectedMarket(resolvedMarket[0]);
    caCtx.updateSelectedCategory(transactionRecord?.category?.[0]);
    caCtx.updateSelectedPersona(
      resolvedPersona[0]?.title ?? resolvedPersona[0] ?? "General Persona"
    );
    caCtx.updateCustomTemplateID(transactionRecord?.userMetricSettingId || null);
    caCtx.updateRbiType(transactionRecord?.rbiType || "propensityToRecommend");
    if (transactionRecord?.keywords) {
      caCtx.updateKeywords(transactionRecord.keywords);
    }
    caCtx.updateRankCompetitiveSet(transactionRecord?.rank_competitive_set || []);
    caCtx.updateRankMethodology(transactionRecord?.rank_methodology || "by_category");
    caCtx.updateName(`${entityLabel} Category Analysis Overview`);
    caCtx.updateId(transactionId);
    caCtx.updatedLaunched(true);

    if (loadCategoryAnalysis) {
      await loadCategoryAnalysis(
        transactionId,
        "brand",
        user?.user_role?.user_id,
        false,
        false,
        true
      );
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      COMPLETED: { color: "success", label: "Completed" },
      RUNNING: { color: "warning", label: "In Progress" },
      PENDING: { color: "warning", label: "In Progress" },
      INITIATED: { color: "warning", label: "In Progress" },
      RETRY: { color: "error", label: "Failed" },
      FAILED: { color: "error", label: "Failed" },
    };
    const config = statusConfig[status] || { color: "default", label: status };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        className={classes.statusChip}
      />
    );
  };

  // Run Type pill — same color pairing used for Discovery's "Discovery Type" column
  // (Full/production = blue, Test/preliminary = violet)
  const getRunTypeChip = (auditType) => {
    const isTest = (auditType || "").toLowerCase() === "test";
    return (
      <Chip
        label={isTest ? "Test" : "Final"}
        size="small"
        sx={{
          fontSize: "11px",
          height: "22px",
          backgroundColor: isTest ? "#ede9fe" : "#e0f2fe",
          color: isTest ? "#6d28d9" : "#0369a1",
          fontWeight: 500,
        }}
      />
    );
  };

  // Test Runs circular badge — red dot indicates at least one failed run
  const getTestRunsBadge = (count, hasFailure) => {
    if (count == null) return <span>-</span>;
    return (
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#e5e7eb",
            color: "#374151",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {count}
        </Box>
        {hasFailure && (
          <Box
            sx={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#dc3545",
              border: "1.5px solid #fff",
            }}
          />
        )}
      </Box>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const renderArrayWithOverflow = (items, maxVisible = 3) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const displayItems = items.map((item) =>
      typeof item === "string" ? item : item?.title || item?.name || String(item)
    );
    const visibleItems = displayItems.slice(0, maxVisible);
    const remainingItems = displayItems.slice(maxVisible);
    return (
      <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
        {visibleItems.map((item, i) => (
          <Box
            key={i}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              fontSize: "12px",
              fontWeight: 500,
              color: "#374151",
            }}
          >
            {item}
          </Box>
        ))}
        {remainingItems.length > 0 && (
          <Tooltip
            title={<Box>{remainingItems.map((item, i) => <div key={i}>{item}</div>)}</Box>}
            arrow
            placement="top"
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor: "#e5e7eb",
                fontSize: "12px",
                fontWeight: 700,
                color: "#4b5563",
                cursor: "help",
                "&:hover": { backgroundColor: "#d1d5db" },
              }}
            >
              +{remainingItems.length}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  const headers = [
    { id: "category", name: "Category", sorting: false },
    { id: "llm", name: "LLM Model", sorting: false },
    { id: "market", name: "Market", sorting: false },
    { id: "persona", name: "Persona", sorting: false },
    { id: "date", name: "Date Run", sorting: false },
    ...(isBaselineCategoryAnalysisEnabled ? [{ id: "caType", name: "Analysis Type", sorting: false }] : []),
    { id: "testRuns", name: "Test Runs", sorting: false },
    { id: "runType", name: "Run Type", sorting: false },
    { id: "status", name: "Status", sorting: false },
    { id: "actions", name: "Actions", sorting: false },
  ];

  const renderRow = (row, index) => {
    // transactionStatus reflects the latest transaction's status; fall back to
    // status for older history records that predate this field.
    const effectiveStatus = row.transactionStatus || row.status;
    const isFailed = effectiveStatus === "RETRY" || effectiveStatus === "FAILED";
    const isSourceValidating =
      row?.sourceValidationStatus === "RUNNING" &&
      ["COMPLETED", "RETRY"].includes(effectiveStatus) &&
      row.run_type === "final";
    const startDateTime = formatDateTime(row?.start_time);

    return (
      <StyledTableRow key={row.transactionId || index}>
        <StyledTableCell>
          <Box className={classes.brandCell}>
            <Box>
              {renderArrayWithOverflow(row.category, 3) ?? "—"}
              {/* Shared-by badge hidden per client request
              {row?.is_shared && (
                <Chip
                  label={row?.owner_name ? `Shared by ${row.owner_name}` : "Shared with you"}
                  size="small"
                  sx={{ ml: 1, fontSize: "10px", height: "18px", backgroundColor: "#ede9fe", color: "#6d28d9" }}
                />
              )} */}
              {isSourceValidating && (
                <div className={classes.validationBadge}>Sources Validating...</div>
              )}
            </Box>
          </Box>
        </StyledTableCell>

        <StyledTableCell>
          {row.llm && Array.isArray(row.llm) ? (
            renderArrayWithOverflow(
              row.llm.map((v) => OVERVIEW_LLM_LABELS[v] || v),
              3
            )
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
                fontSize: "12px",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              {OVERVIEW_LLM_LABELS[row?.primaryLLM] || ""}
            </Box>
          )}
        </StyledTableCell>

        <StyledTableCell>
          {renderArrayWithOverflow(
            (row.market || []).map((m) =>
              m === "GeneralMarket" ? "General Market" : m
            ),
            3
          )}
        </StyledTableCell>

        <StyledTableCell>
          {renderArrayWithOverflow(row.persona, 3)}
        </StyledTableCell>

        <StyledTableCell title={startDateTime}>
          {formatDate(row.start_time)}
        </StyledTableCell>

        {isBaselineCategoryAnalysisEnabled && (
          <StyledTableCell>
            <Chip
              label={row.caMode === "baseline" ? "Test" : "Full"}
              size="small"
              sx={{
                fontSize: "11px",
                height: "22px",
                backgroundColor: row.caMode === "baseline" ? "#ede9fe" : "#e0f2fe",
                color: row.caMode === "baseline" ? "#6d28d9" : "#0369a1",
                fontWeight: 500,
              }}
            />
          </StyledTableCell>
        )}

        <StyledTableCell>
          {getTestRunsBadge(row.total_test_runs, isFailed)}
        </StyledTableCell>

        <StyledTableCell>
          {getRunTypeChip(row.audit_type || row.run_type)}
        </StyledTableCell>

        <StyledTableCell>{getStatusChip(effectiveStatus)}</StyledTableCell>

        <StyledTableCell>
          <Box className={classes.actionButtons}>
            <IconButton
              size="small"
              className={classes.actionIcon}
              onClick={() => getTransactionData(row)}
              title="View Details"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              className={`${classes.actionIcon} ${classes.deleteIcon}`}
              onClick={() => handleDeleteClick(row)}
              title="Delete"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </StyledTableCell>
      </StyledTableRow>
    );
  };

  const displayData = isSearchMode ? searchResults : history;
  const currentLoading = isSearchMode ? searchLoading : loading;
  const currentTotalCount = isSearchMode ? searchTotalCount : totalCount;
  const currentTotalPages = isSearchMode ? searchTotalPages : totalPages;
  const calculatedTotalPages =
    currentTotalPages || Math.ceil((currentTotalCount || 0) / PAGE_LIMIT);
  const startIndex = (page - 1) * PAGE_LIMIT;
  const endIndex = Math.min(page * PAGE_LIMIT, currentTotalCount || displayData.length);

  return (
    <>
      <Modal
        handleClose={onClose}
        open={open}
        title="Category Analysis History"
        width="1500"
        showActions={false}
      >
        <Box className={classes.historyContainer}>
          <Box className={classes.headerContainer}>
            <TextField
              placeholder="Search by category, LLM, market, or persona..."
              value={searchQuery}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                      className={classes.clearButton}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className={classes.searchField}
            />
          </Box>

          <Box className={classes.tableWrapper}>
            {currentLoading && displayData.length === 0 ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            ) : (
              <CustomizedTables
                headers={headers}
                data={displayData}
                render={renderRow}
                elevation={0}
                size="medium"
              />
            )}
          </Box>

          {(currentTotalCount > 0 || displayData.length > 0) && (
            <Box className={classes.paginationContainer}>
              <div className={classes.resultsText}>
                {currentLoading ? (
                  "Loading..."
                ) : (
                  <>
                    Showing{" "}
                    <span className={classes.boldText}>{startIndex + 1}</span>{" "}
                    to{" "}
                    <span className={classes.boldText}>{endIndex}</span> of{" "}
                    <span className={classes.boldText}>{currentTotalCount || 0}</span>{" "}
                    results
                    {isSearchMode && (
                      <span className={classes.searchModeIndicator}> (filtered)</span>
                    )}
                  </>
                )}
              </div>
              {calculatedTotalPages > 1 && (
                <Pagination
                  count={calculatedTotalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  className={classes.pagination}
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  disabled={currentLoading}
                />
              )}
            </Box>
          )}

          {!currentLoading && !error && displayData.length === 0 && (
            <Box className={classes.emptyState}>
              <p>{isSearchMode ? "No results found." : "No history yet."}</p>
            </Box>
          )}
          {error && displayData.length === 0 && (
            <Box className={classes.errorState}>
              <h3>Error</h3>
              <p>{error}</p>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal
        handleClose={() => {
          setShowDeleteDialog(false);
          setItemToDelete(null);
        }}
        handleConfirm={deleteHistoryItemHandler}
        open={showDeleteDialog}
        title="Delete History"
        showActions
        confirmBtnText="Delete"
        confirmBtnColor="error"
      >
        <div className={classes.contentText}>
          Do you want to delete this record?
        </div>
      </Modal>
    </>
  );
}
